/**
 * Utility for managing print window creation and React component mounting
 * Provides cleaner window management without setTimeout hacks
 */

/**
 * Create a print window and mount a React component
 * @param {React.Component} Component - React component to render
 * @param {Object} props - Props to pass to the component
 * @param {string} title - Window title
 * @returns {Window|null} The created window, or null if failed
 */
export const createPrintWindow = (Component, props, title = 'Cooking Plan') => {
    const printWindow = window.open('', '_blank', 'width=1000,height=800');
    
    if (!printWindow) {
        console.error('Failed to open print window. Please allow popups.');
        return null;
    }

    // Critical CSS to inline (pixel-art timeline styles)
    const criticalCSS = `
        :root {
            --scale: 10;
            --makespan: 0;
            --c-text: #1e1e1e;
            --c-sub: #555;
            --c-grid: #e5e5e5;
            --c-card: #fafafa;
            --c-border: #222;
            --c-active: #1f4b99;
            --c-inactive: #f0f0f0;
            --c-inactive-stroke: #9a9a9a;
            --c-conflict: #b00020;
            --c-accent: #6b4fd8;
            --r-unit: 28px;
            --r-gap: 8px;
            --font-display: "Courier New", monospace;
        }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; color: var(--c-text); font: 14px/1.4 var(--font-display); }
        .pp-wrap { padding: 20px; }
        .pp-title { font-size: 28px; margin: 0 0 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
        .pp-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; margin-bottom: 14px; }
        .pp-card { background: var(--c-card); border: 2px solid var(--c-border); padding: 10px 12px; }
        .pp-card .k { display: block; font-size: 10px; color: var(--c-sub); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em; }
        .pp-card .v { font-weight: 700; font-size: 14px; color: var(--c-text); }
        .pp-legend { display: flex; gap: 20px; align-items: center; margin: 6px 0 16px; flex-wrap: wrap; }
        .legend-item { display: flex; gap: 8px; align-items: center; font-size: 12px; }
        .icon { width: 14px; height: 14px; display: inline-block; vertical-align: middle; flex-shrink: 0; }
        .pp-minimap { height: 8px; border: 2px solid var(--c-border); position: relative; margin: 10px 0 16px; background: var(--c-card); }
        .pp-minimap .tick { position: absolute; top: -6px; width: 1px; height: 20px; background: var(--c-grid); }
        .pp-schedule { border-top: 2px solid var(--c-border); padding-top: 10px; margin-top: 6px; }
        .pp-schedule-container { display: flex; gap: 12px; position: relative; }
        .pp-time-ruler-vertical { position: sticky; left: 0; width: 80px; border-right: 2px solid var(--c-border); position: relative; background: var(--c-card); z-index: 10; flex-shrink: 0; }
        .pp-ruler-tick-vertical { position: absolute; left: 0; width: 100%; }
        .pp-ruler-tick-line-vertical { position: absolute; left: 0; top: 0; width: 100%; height: 1px; background: var(--c-grid); }
        .pp-ruler-tick-label-vertical { position: absolute; left: 4px; top: -8px; font-size: 10px; color: var(--c-sub); white-space: nowrap; background: var(--c-card); padding: 2px 4px; font-weight: 700; font-family: var(--font-display); }
        .pp-columns { display: grid !important; grid-template-columns: repeat(var(--recipe-count, 3), 1fr) !important; gap: 8px; flex: 1; position: relative; }
        .pp-recipes-header { display: block !important; width: 100%; visibility: visible !important; }
        .pp-recipes-header-cards { display: grid !important; grid-template-columns: repeat(var(--recipe-count, 3), 1fr) !important; gap: 8px; }
        .pp-recipe-header-card { display: block !important; background: var(--c-card) !important; border: 2px solid var(--c-border) !important; padding: 12px; }
        .pp-column { display: flex; flex-direction: column; border: 2px solid var(--c-border); background: var(--c-card); }
        .pp-column-title { font-weight: 700; padding: 12px; border-bottom: 2px solid var(--c-border); font-size: 16px; background: var(--c-card); text-align: center; position: sticky; top: 0; z-index: 5; }
        .pp-column-grid { position: relative; min-height: 400px; border-left: 1px solid var(--c-grid); border-right: 1px solid var(--c-grid); overflow-y: visible; overflow-x: hidden; }
        .pp-column-grid::before { content: ""; position: absolute; inset: 0; background: repeating-linear-gradient(to bottom, transparent 0 calc(var(--scale) * 1px), var(--c-grid) calc(var(--scale) * 1px) calc(var(--scale) * 1px + 1px)); opacity: 0.5; pointer-events: none; }
        .pp-segment-vertical { position: absolute; left: 8px; right: 8px; border: 2px solid var(--c-border); overflow: hidden; z-index: 2; min-height: 60px; }
        .pp-segment-vertical[data-type="active"] { background: var(--c-active); color: #fff; border-color: var(--c-active); }
        .pp-segment-vertical[data-type="inactive"] { background: repeating-linear-gradient(45deg, #fff 0 6px, #eaeaea 6px 12px); color: var(--c-text); border-color: var(--c-inactive-stroke); }
        .pp-segment-vertical-content { padding: 10px; display: flex; flex-direction: column; gap: 8px; height: 100%; min-height: 60px; }
        .pp-segment-vertical-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
        .pp-segment-vertical-step { font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; font-size: 11px; flex-shrink: 0; }
        .pp-segment-vertical-type { font-size: 9px; text-transform: uppercase; letter-spacing: 0.1em; padding: 2px 6px; border: 1px solid currentColor; font-weight: 700; flex-shrink: 0; }
        .pp-segment-vertical-name { font-size: 13px; font-weight: 600; line-height: 1.4; flex: 1; word-wrap: break-word; overflow-wrap: break-word; }
        .pp-segment-vertical-time { display: flex; gap: 6px; align-items: center; font-size: 10px; opacity: 0.9; font-family: var(--font-display); margin-top: auto; }
        .loading-container { display: flex; align-items: center; justify-content: center; height: 100vh; font-family: var(--font-display); color: #666; }
        .loading-spinner { border: 3px solid #f3f3f3; border-top: 3px solid #1b263b; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 0 auto 16px; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @media print { @page { size: Letter; margin: 0.5in; } .pp-wrap { padding: 0; } .pp-time-ruler { margin-left: 160px; } .pp-lane { grid-template-columns: 160px 1fr; } }
    `;

    // Write HTML structure with loading state and critical CSS
    printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${title}</title>
            <style>${criticalCSS}</style>
        </head>
        <body>
            <div id="root">
                <div class="loading-container">
                    <div style="text-align: center;">
                        <div class="loading-spinner"></div>
                        <p>Loading print preview...</p>
                    </div>
                </div>
            </div>
        </body>
        </html>
    `);
    printWindow.document.close();

    // Wait for DOM to be ready, then mount React
    // Use setTimeout to ensure the document is fully ready
    setTimeout(() => {
        try {
            // Use require to get React and ReactDOM in the new window context
            // This works because webpack provides require in the bundle
            const { createRoot } = require('react-dom/client');
            const React = require('react');
            
            const rootElement = printWindow.document.getElementById('root');
            if (!rootElement) {
                throw new Error('Root element not found');
            }
            
            // Clear the loading content
            rootElement.innerHTML = '';
            
            // Create React root and render
            const root = createRoot(rootElement);
            root.render(
                React.createElement(Component, props)
            );
            
            // After rendering, inject the full CSS from the component
            // The component imports printablePlan.css, but we need to ensure it's available
            // We'll inject it via a style tag after a short delay to ensure component is rendered
            setTimeout(() => {
                // Try to get styles from the rendered component's stylesheet
                // For now, we rely on the critical CSS already inlined
                // The component's CSS import should work if webpack processes it
                // But as a fallback, we ensure the critical CSS covers everything needed
            }, 200);
        } catch (error) {
            console.error('Error mounting component in print window:', error);
            const rootElement = printWindow.document.getElementById('root');
            if (rootElement) {
                rootElement.innerHTML = `
                    <div style="padding: 20px; font-family: system-ui, sans-serif;">
                        <h1>Error Loading Print Preview</h1>
                        <p>There was an error generating the print preview. Please try again.</p>
                        <p style="color: #666; font-size: 12px;">${error.message}</p>
                        <details style="margin-top: 16px;">
                            <summary style="cursor: pointer; color: #999;">Error Details</summary>
                            <pre style="font-size: 10px; color: #999; margin-top: 8px; white-space: pre-wrap;">${error.stack || error.toString()}</pre>
                        </details>
                    </div>
                `;
            }
        }
    }, 100);

    return printWindow;
};

/**
 * Show loading state in print window while component loads
 * @param {Window} printWindow - The print window
 * @deprecated Loading is now handled in createPrintWindow
 */
export const showPrintLoading = (printWindow) => {
    // Loading is now handled in createPrintWindow
    // This function is kept for backwards compatibility but does nothing
    if (!printWindow) return;
};
